import React, { useEffect } from 'react';
import { lunchMenu } from '../data/menuData';
import { getLunchWeek } from '../utils/lunchWeek';
import { Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useDailyMenus } from '../hooks/useDailyMenus';
import { LUNCH_SITE_URL } from '../utils/lunchLocale';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'weekend'];

const PRINT_CSS = `
@page { size: A4 portrait; margin: 9mm; }
@media print {
  body * { visibility: hidden; }
  #print-sheet, #print-sheet * { visibility: visible; }
  #print-sheet { position: absolute; left: 0; top: 0; width: 100%; min-height: 0 !important; box-shadow: none !important; margin: 0 !important; }
  .no-print { display: none !important; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}`;

const Diet = ({ d }) => (d && d.length ? <span className="text-[8.5px] text-[#6B705C] font-semibold ml-1">({d.join(', ')})</span> : null);

const DayBlock = ({ dayKey, menu }) => {
  const [title, theme] = (menu.dayFi || menu.day).split('·').map((s) => s.trim());
  return (
    <div className="border border-[#D9D9D6] rounded-md px-3 py-2.5 break-inside-avoid" data-testid={`print-day-${dayKey}`}>
      <div className="flex items-baseline justify-between border-b border-[#C97A2B]/40 pb-1 mb-1">
        <p className="text-[13px] font-bold text-[#1A1A1A] uppercase tracking-wider">
          {title} <span className="normal-case font-normal italic text-[#C97A2B]">· {theme}</span>
        </p>
        <span className="text-[8px] text-[#6B705C] whitespace-nowrap ml-2">{menu.hours.replace('-', '–')}</span>
      </div>
      <ul className="space-y-[3px]">
        {menu.mains.map((m, i) => (
          <li key={i} className="text-[11px] leading-snug text-[#2F2F2F]">
            <span className="font-semibold">{m.nameFi || m.name}</span>
            {m.chefPick && <span className="ml-1 text-[7.5px] font-bold uppercase text-white bg-[#C97A2B] px-1.5 py-[1px] rounded">Kokin suositus</span>}
            <Diet d={m.dietary} />
          </li>
        ))}
      </ul>
      <p className="text-[9px] text-[#6B705C] mt-1.5 leading-snug">
        {menu.sides.map((s) => s.nameFi || s.name).join(' · ')}
      </p>
    </div>
  );
};

const PrintableLunchMenu = () => {
  const { label } = getLunchWeek();
  const dailyMenus = useDailyMenus();
  const p = lunchMenu.priceInfo;

  useEffect(() => { document.title = `Sunsari lounaslista – ${label}`; }, [label]);

  return (
    <div className="min-h-screen bg-[#E9E6E0] py-6 px-2" data-testid="printable-lunch-page">
      <style>{PRINT_CSS}</style>
      <div className="no-print max-w-[210mm] mx-auto mb-4 flex items-center justify-between gap-3">
        <a href="/menu" className="text-sm text-[#2F2F2F] underline" data-testid="print-back-link">← Takaisin lounaslistaan</a>
        <button onClick={() => window.print()} data-testid="print-now-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#C97A2B] text-white text-sm font-semibold hover:bg-[#B65A3A] transition-colors">
          <Printer className="w-4 h-4" /> Tulosta A4
        </button>
      </div>

      <div id="print-sheet" className="bg-white max-w-[210mm] min-h-[297mm] mx-auto shadow-xl px-[9mm] py-[6mm] text-[#2F2F2F]" data-testid="print-sheet">
        <header className="text-center mb-2">
          <p className="text-[9px] tracking-[0.35em] uppercase text-[#C97A2B] font-bold">Ravintola Sunsari</p>
          <h1 className="text-[32px] leading-tight text-[#1A1A1A]" style={{ fontFamily: 'DM Serif Display, serif' }}>Viikon lounasbuffet</h1>
          <p className="text-[14px] font-semibold text-[#1A1A1A]" data-testid="print-week-label">{label}</p>
          <p className="text-[10px] text-[#6B705C] mt-0.5">
            Aitoja Himalajan makuja · nepalilaisia erikoisuuksia · valmistettu tuoreena joka päivä
          </p>
        </header>

        <div className="bg-[#F5F3EF] rounded-md px-3 py-1.5 mb-2 text-center text-[10px] leading-snug">
          Jokaiseen buffetiin sisältyy <b>yli 17 käsintehtyä salaattia</b>, basmatiriisi, nepalilainen riisi, tuore tandoori-naan,
          raita, mango lassi, nepalilainen chai sekä <b>päivän käsintehty jälkiruoka</b>.
        </div>

        <div className="grid grid-cols-2 gap-2">
          {DAY_KEYS.filter((k) => dailyMenus[k]).map((k) => <DayBlock key={k} dayKey={k} menu={dailyMenus[k]} />)}
        </div>

        <footer className="mt-2 pt-2 border-t border-[#D9D9D6] grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2 text-center text-[10px]">
          <div><b className="text-[13px]">Ma–Pe {p.regular}</b><br />Opiskelijat {p.student} · Eläkeläiset {p.pensioner}<br />klo 11–15</div>
          <div><b className="text-[13px]">La {p.weekend.regular}</b><br />Opiskelijat {p.weekend.student} · Eläkeläiset {p.weekend.pensioner}<br />klo 12–16</div>
          <div><b className="text-[13px]">040 377 9200</b><br />Hietasaarenkatu 18, Vaasa<br />ravintolasunsari.com</div>
          <div className="flex flex-col items-center" data-testid="print-qr">
            <QRCodeSVG value={LUNCH_SITE_URL} size={60} level="M" fgColor="#1A1A1A" />
            <span className="text-[7.5px] font-semibold mt-0.5 leading-tight">Skannaa lounaslista<br />puhelimeesi</span>
          </div>
        </footer>
        <p className="text-center text-[9px] text-[#6B705C] mt-2">
          L = laktoositon · G = gluteeniton · V = vegaaninen · Kysy henkilökunnalta allergeeneista.
        </p>
      </div>
    </div>
  );
};

export default PrintableLunchMenu;
